import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, Edit2Icon, Trash2Icon, XIcon, SaveIcon, Loader2Icon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { MenuItemRecipe, InventoryItem } from './types';
interface Props {
  restaurantId: string;
}
interface MenuItem {
  id: string;
  name: string;
  category: string;
}
export function RecipesTable({
  restaurantId
}: Props) {
  const [recipes, setRecipes] = useState<MenuItemRecipe[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Partial<MenuItemRecipe> | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch recipes with joined names
      const {
        data: recipesData,
        error: recipesError
      } = await supabase.from('menu_item_recipes').select(`
          *,
          menu_items!inner(name, restaurant_id),
          inventory_items!inner(ingredient_name, unit)
        `).eq('menu_items.restaurant_id', restaurantId);
      if (recipesError) throw recipesError;
      // Fetch menu items for dropdown
      const {
        data: menuData,
        error: menuError
      } = await supabase.from('menu_items').select('id, name, category').eq('restaurant_id', restaurantId).order('name');
      if (menuError) throw menuError;
      // Fetch inventory items for dropdown
      const {
        data: invData,
        error: invError
      } = await supabase.from('inventory_items').select('*').eq('restaurant_id', restaurantId).eq('is_active', true).order('ingredient_name');
      if (invError) throw invError;
      setRecipes(recipesData || []);
      setMenuItems(menuData || []);
      setInventoryItems(invData || []);
    } catch (err) {
      console.error('Error fetching recipe data:', err);
      showToast('Error al cargar recetas', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [restaurantId]);
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRecipe?.menu_item_id || !editingRecipe?.ingredient_id || !editingRecipe?.qty_per_item) {
      showToast('Plato, ingrediente y cantidad son requeridos', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        id: editingRecipe.id,
        menu_item_id: editingRecipe.menu_item_id,
        ingredient_id: editingRecipe.ingredient_id,
        qty_per_item: Number(editingRecipe.qty_per_item),
        notes: editingRecipe.notes || null
      };
      const {
        error
      } = await supabase.from('menu_item_recipes').upsert(payload);
      if (error) throw error;
      showToast('Receta guardada exitosamente', 'success');
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      console.error('Error saving recipe:', err);
      showToast('Error al guardar receta', 'error');
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este ingrediente de la receta?')) return;
    try {
      const {
        error
      } = await supabase.from('menu_item_recipes').delete().eq('id', id);
      if (error) throw error;
      showToast('Línea de receta eliminada', 'success');
      fetchData();
    } catch (err) {
      console.error('Error deleting recipe:', err);
      showToast('Error al eliminar', 'error');
    }
  };
  const openModal = (recipe?: MenuItemRecipe) => {
    if (recipe) {
      setEditingRecipe(recipe);
    } else {
      setEditingRecipe({
        menu_item_id: '',
        ingredient_id: '',
        qty_per_item: 0,
        notes: ''
      });
    }
    setIsModalOpen(true);
  };
  // Group recipes by menu item
  const groupedRecipes = recipes.reduce((acc, recipe) => {
    const menuName = recipe.menu_items?.name || 'Desconocido';
    if (!acc[menuName]) acc[menuName] = [];
    acc[menuName].push(recipe);
    return acc;
  }, {} as Record<string, MenuItemRecipe[]>);
  // Filter groups based on search
  const filteredGroups = Object.entries(groupedRecipes).filter(([menuName, items]) => {
    if (menuName.toLowerCase().includes(search.toLowerCase())) return true;
    return items.some(item => item.inventory_items?.ingredient_name.toLowerCase().includes(search.toLowerCase()));
  });
  return <div className="space-y-4" data-id="element-366">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-367">
        <div className="relative w-full sm:w-72" data-id="element-368">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" data-id="element-369" />
          <input type="text" placeholder="Buscar plato o ingrediente..." value={search} onChange={e => setSearch(e.target.value)} className="w-full bg-surface border border-gray-800 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-dorado transition-colors" data-id="element-370" />
        </div>
        <button onClick={() => openModal()} className="flex items-center gap-2 bg-dorado text-carbon px-4 py-2 rounded-lg text-sm font-bold hover:bg-dorado/90 transition-colors w-full sm:w-auto justify-center" data-id="element-371">
          <PlusIcon className="w-4 h-4" data-id="element-372" />
          Agregar a Receta
        </button>
      </div>

      {/* Toast Notification */}
      {toast && <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 animate-fade-in-up ${toast.type === 'success' ? 'bg-emerald-500/90 text-white' : 'bg-red-500/90 text-white'}`} data-id="element-373">
          {toast.type === 'success' ? <CheckCircleIcon className="w-5 h-5" data-id="element-374" /> : <AlertTriangleIcon className="w-5 h-5" data-id="element-375" />}
          <span className="font-medium" data-id="element-376">{toast.message}</span>
        </div>}

      {/* Recipes List */}
      {loading ? <div className="flex justify-center py-12 bg-surface border border-gray-800 rounded-xl" data-id="element-377">
          <Loader2Icon className="w-8 h-8 text-dorado animate-spin" data-id="element-378" />
        </div> : filteredGroups.length === 0 ? <div className="text-center py-12 bg-surface border border-gray-800 rounded-xl text-gray-500" data-id="element-379">
          No se encontraron recetas.
        </div> : <div className="space-y-4" data-id="element-380">
          {filteredGroups.map(([menuName, items]) => <div key={menuName} className="bg-surface border border-gray-800 rounded-xl overflow-hidden" data-id="element-381">
              <div className="bg-surface-light px-4 py-3 border-b border-gray-800 flex justify-between items-center" data-id="element-382">
                <h3 className="font-heading font-bold text-white text-lg" data-id="element-383">
                  {menuName}
                </h3>
                <span className="text-xs text-gray-400 bg-carbon px-2 py-1 rounded-md border border-gray-800" data-id="element-384">
                  {items.length} ingredientes
                </span>
              </div>
              <div className="overflow-x-auto" data-id="element-385">
                <table className="w-full text-left text-sm" data-id="element-386">
                  <thead className="text-gray-500 border-b border-gray-800/50" data-id="element-387">
                    <tr data-id="element-388">
                      <th className="px-4 py-2 font-medium w-1/3" data-id="element-389">
                        Ingrediente
                      </th>
                      <th className="px-4 py-2 font-medium text-right w-1/4" data-id="element-390">
                        Cantidad
                      </th>
                      <th className="px-4 py-2 font-medium w-1/3" data-id="element-391">Notas</th>
                      <th className="px-4 py-2 font-medium text-center" data-id="element-392">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50" data-id="element-393">
                    {items.map(item => <tr key={item.id} className="hover:bg-surface-light/50 transition-colors" data-id="element-394">
                        <td className="px-4 py-2 text-gray-300" data-id="element-395">
                          {item.inventory_items?.ingredient_name}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-dorado" data-id="element-396">
                          {item.qty_per_item}{' '}
                          <span className="text-gray-500 text-xs" data-id="element-397">
                            {item.inventory_items?.unit}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-500 text-xs" data-id="element-398">
                          {item.notes || '-'}
                        </td>
                        <td className="px-4 py-2 text-center" data-id="element-399">
                          <div className="flex items-center justify-center gap-2" data-id="element-400">
                            <button onClick={() => openModal(item)} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors" title="Editar" data-id="element-401">
                              <Edit2Icon className="w-4 h-4" data-id="element-402" />
                            </button>
                            <button onClick={() => handleDelete(item.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors" title="Eliminar" data-id="element-403">
                              <Trash2Icon className="w-4 h-4" data-id="element-404" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>)}
        </div>}

      {/* Modal */}
      {isModalOpen && editingRecipe && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-id="element-405">
          <div className="bg-surface border border-gray-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col" data-id="element-406">
            <div className="flex items-center justify-between p-4 border-b border-gray-800" data-id="element-407">
              <h3 className="text-lg font-heading font-bold text-white" data-id="element-408">
                {editingRecipe.id ? 'Editar Receta' : 'Agregar a Receta'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white" data-id="element-409">
                <XIcon className="w-5 h-5" data-id="element-410" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto" data-id="element-411">
              <form id="recipe-form" onSubmit={handleSave} className="space-y-4" data-id="element-412">
                <div data-id="element-413">
                  <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-414">
                    Plato del Menú *
                  </label>
                  <select required value={editingRecipe.menu_item_id || ''} onChange={e => setEditingRecipe({
                ...editingRecipe,
                menu_item_id: e.target.value
              })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none" disabled={!!editingRecipe.id} // Don't allow changing menu item when editing
              data-id="element-415">
                    <option value="" data-id="element-416">Selecciona un plato...</option>
                    {menuItems.map(item => <option key={item.id} value={item.id} data-id="element-417">
                        {item.name} ({item.category})
                      </option>)}
                  </select>
                </div>

                <div data-id="element-418">
                  <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-419">
                    Ingrediente *
                  </label>
                  <select required value={editingRecipe.ingredient_id || ''} onChange={e => setEditingRecipe({
                ...editingRecipe,
                ingredient_id: e.target.value
              })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none" data-id="element-420">
                    <option value="" data-id="element-421">Selecciona un ingrediente...</option>
                    {inventoryItems.map(item => <option key={item.id} value={item.id} data-id="element-422">
                        {item.ingredient_name} ({item.unit})
                      </option>)}
                  </select>
                </div>

                <div data-id="element-423">
                  <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-424">
                    Cantidad por Plato *
                    {editingRecipe.ingredient_id && <span className="text-dorado ml-1" data-id="element-425">
                        (en{' '}
                        {inventoryItems.find(i => i.id === editingRecipe.ingredient_id)?.unit}
                        )
                      </span>}
                  </label>
                  <input type="number" required min="0" step="0.001" value={editingRecipe.qty_per_item || ''} onChange={e => setEditingRecipe({
                ...editingRecipe,
                qty_per_item: parseFloat(e.target.value)
              })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none" data-id="element-426" />
                </div>

                <div data-id="element-427">
                  <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-428">
                    Notas (Opcional)
                  </label>
                  <textarea rows={2} value={editingRecipe.notes || ''} onChange={e => setEditingRecipe({
                ...editingRecipe,
                notes: e.target.value
              })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none resize-none" data-id="element-429" />
                </div>
              </form>
            </div>

            <div className="p-4 border-t border-gray-800 flex justify-end gap-3 bg-surface-light" data-id="element-430">
              <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition-colors" data-id="element-431">
                Cancelar
              </button>
              <button type="submit" form="recipe-form" disabled={saving} className="flex items-center gap-2 bg-dorado text-carbon px-4 py-2 rounded-lg text-sm font-bold hover:bg-dorado/90 transition-colors disabled:opacity-50" data-id="element-432">
                {saving ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-433" /> : <SaveIcon className="w-4 h-4" data-id="element-434" />}
                Guardar
              </button>
            </div>
          </div>
        </div>}
    </div>;
}